import React, { useState, useEffect } from 'react';
import axios from '../../../configApi/axiosConfig';
import { APIURL } from '../../../configApi/apiConfig';

export const DetalleFactura = ({ idCompra, isInline = false }) => {
    const [factura, setFactura] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);

    useEffect(() => {
        const storedToken = localStorage.getItem("token");
        setToken(storedToken);
    }, []);

    useEffect(() => {
        if (!idCompra || !token) return;
        
        const fetchFactura = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.post(APIURL.getViewDetalleFacturaOperacion(), { idCompra }, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    }
                });
                if (response.data && Array.isArray(response.data)) {
                    setFactura(response.data);
                } else if (response.data && response.data.data) {
                    setFactura(response.data.data);
                } else {
                    setFactura([]);
                }
            } catch (err) {
                console.error("Error fetching detalle factura:", err);
                setError(err.response?.data?.error || 'No se pudo cargar el detalle de la factura.');
            } finally {
                setLoading(false);
            }
        };
        fetchFactura();
    }, [idCompra, token]);

    const COLUMNAS = [
        { label: 'Código', key: 'Codigo', align: 'left', className: 'font-semibold text-slate-700' },
        { label: 'Artículo', key: 'Articulo', align: 'left' },
        { label: 'Cantidad', key: 'Cantidad', align: 'center', format: 'number' },
        { label: 'Precio', key: 'Precio', align: 'right', format: 'currency' },
        { label: 'IVA', key: 'Impuesto', align: 'right', format: 'currency' },
        { label: '% Desc.', key: 'DescuentoPorc', align: 'right', format: 'percent' },
        { label: 'Subtotal', key: 'SubTotal', align: 'right', format: 'currency', className: 'font-bold text-slate-800' }
    ];

    const formatValue = (value, format) => {
        const val = parseFloat(value || 0);
        if (format === 'currency') return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        if (format === 'percent') return `${val.toFixed(2)}%`;
        if (format === 'number') return val.toLocaleString('en-US');
        return value || '-';
    };

    const calcularTotal = () => {
        return factura.reduce((sum, item) => sum + parseFloat(item.SubTotal || 0), 0);
    };

    const content = (
        <div className={`bg-white shadow-2xl p-0 space-y-0 ${!isInline ? 'rounded-3xl max-w-7xl w-full flex flex-col' : 'w-full flex flex-col'} border border-slate-100`}>
            <div className="relative bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-900 px-6 py-5 border-b border-blue-800 flex flex-col gap-4 overflow-hidden rounded-t-3xl">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-400 rounded-full mix-blend-screen filter blur-3xl"></div>
                </div>
                <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-1.5 h-7 bg-gradient-to-b from-blue-400 to-blue-500 rounded-full shadow-[0_0_10px_rgba(96,165,250,0.5)]"></div>
                        <h2 className="text-white text-lg font-bold tracking-wide flex items-center gap-2">
                            Detalle de Factura <span className="px-3 py-1 bg-blue-500/20 border border-blue-400/30 rounded-lg text-blue-200 text-sm tracking-widest shadow-inner">#{idCompra}</span>
                        </h2>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-x-auto overflow-y-auto bg-slate-50/50">
                <table className="w-full border-collapse text-[12px]">
                    <thead className="sticky top-0 z-20">
                        <tr className="bg-white shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
                            {COLUMNAS.map(col => (
                                <th key={col.key} className={`px-4 py-3 text-${col.align} font-bold text-[11px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200 whitespace-nowrap bg-white/90 backdrop-blur-sm`}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {factura.map((item, index) => (
                            <tr key={index} className="bg-white hover:bg-blue-50/60 transition-all duration-300 hover:shadow-[inset_4px_0_0_0_#3b82f6] group">
                                {COLUMNAS.map(col => (
                                    <td key={col.key} className={`px-4 py-3 text-${col.align} ${col.className || 'text-slate-600'} whitespace-nowrap group-hover:text-slate-900 transition-colors`}>
                                        {formatValue(item[col.key], col.format)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {factura.length === 0 && (
                            <tr>
                                <td colSpan={COLUMNAS.length} className="px-4 py-16">
                                    <div className="flex flex-col items-center justify-center text-slate-400">
                                        <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 border-dashed">
                                            <span className="text-2xl">🧾</span>
                                        </div>
                                        <p className="font-semibold text-slate-500 text-sm tracking-wide">No hay registros</p>
                                        <p className="text-xs mt-1">No se encontró detalle de factura para esta operación.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Sección inferior con el Total */}
            {factura.length > 0 && (
                <div className="bg-white border-t border-slate-200 px-6 py-4 flex justify-end items-center rounded-b-3xl">
                    <div className="flex items-center gap-4 bg-slate-50 px-6 py-3 rounded-2xl border border-slate-200">
                        <span className="text-slate-500 font-bold tracking-widest uppercase text-xs">Total</span>
                        <span className="text-2xl font-black text-blue-900">{formatValue(calcularTotal(), 'currency')}</span>
                    </div>
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-48 bg-white/50 rounded-2xl">
                <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-3"></div>
                <span className="text-sm font-medium text-slate-500 animate-pulse">Cargando factura...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50/50 border border-red-200 rounded-2xl text-red-600 flex items-center gap-3">
                <span className="text-xl">⚠️</span>
                <p className="text-sm font-medium">{error}</p>
            </div>
        );
    }

    if (!isInline) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm p-4 sm:p-6 md:p-12">
                {content}
            </div>
        );
    }

    return content;
};
